import arrowUp from '@/assets/svgs/icons/arrow-up.svg';
import { PromptAttachment } from '@/types/agentChat';
import { AgentRunAttachment } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import { classNames } from '@superfunnel/utils';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import React, {
  DragEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { SquareCursor } from '@/assets/svgs/comps/SquareCursor';
import { ChatInputContextSelectButton } from '@/components/AgentChat/ChatInputContextSelectButton';
import { ChatInputContextsList } from '@/components/AgentChat/ChatInputContextsList';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { useAuthContext } from '@/contexts/AuthContext';
import { UserContext } from '@/contexts/UserContext.tsx';
import { agentFilesStorage } from '@/Firebase';
import { compressImageForUpload } from '@/utils/compressImageForUpload';
import { ChatInputSelectCampaign } from './ChatInputSelectCampaign';

// File validation constants
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const BLOCKED_MIME_TYPES = [
  'video/',
  'audio/',
  'application/zip',
  'application/x-zip-compressed',
  'application/x-rar-compressed',
  'application/vnd.rar',
  'application/x-7z-compressed',
  'application/x-tar',
  'application/gzip',
  'application/x-bzip2',
];

const isBlockedMimeType = (type: string): boolean => {
  return BLOCKED_MIME_TYPES.some(
    (blocked) => type === blocked || type.startsWith(blocked),
  );
};

export type ContextInput = {
  type: 'file' | 'campaign' | 'element' | 'asset';
  location: 'dropdown' | 'inline';
};

interface AgentChatInputProps {
  onSendMessage?: (message: string, attachments?: AgentRunAttachment[]) => void;
  onStopMessage?: () => void;
  className?: string;
  size?: 'sm' | 'lg';
  /**
   * Visual variant of the input.
   * - 'white': Default, no border, clean white background
   * - 'outline': Has a visible border (border-base-200)
   */
  variant?: 'white' | 'outline';
  disabled?: boolean;
  isStreaming?: boolean;
  placeholder?: string;
  isVisualEditMode?: boolean;
  onToggleVisualEdit?: () => void;
  contextTypes: ContextInput[];
  collapsed?: boolean;
  /**
   * The list of prompt attachments (files, campaigns, elements, assets) attached to the input.
   */
  promptContexts: PromptAttachment[];
  /**
   * Callback to update the prompt attachments list.
   */
  setPromptContexts: (contexts: PromptAttachment[]) => void;
  /**
   * Callback to update a single prompt attachment by ID.
   */
  updatePromptContext: (id: string, updates: Partial<PromptAttachment>) => void;
  /**
   * Callback to add a new prompt attachment.
   */
  addPromptContext: (context: PromptAttachment) => void;
  /**
   * Optional: User message history for arrow-key navigation.
   * If not provided, message history navigation is disabled.
   */
  userMessageHistory?: string[];
  /**
   * Optional: Thread ID for file uploads. Defaults to 'default'.
   */
  threadId?: string;
  /**
   * Optional: Default value to prefill the input field.
   */
  defaultValue?: string;
  /** Optional: Maximum number of lines for the textarea before scrolling */
  maxLines?: number;
}

export function AgentChatInput({
  onSendMessage,
  onStopMessage,
  className,
  size = 'sm',
  variant = 'white',
  disabled = false,
  isStreaming = false,
  placeholder,
  isVisualEditMode = false,
  onToggleVisualEdit,
  contextTypes,
  collapsed = false,
  promptContexts,
  setPromptContexts,
  updatePromptContext,
  addPromptContext,
  userMessageHistory = [],
  threadId = 'default',
  defaultValue = '',
  maxLines,
}: AgentChatInputProps) {
  const { track } = useAnalytics();
  const { firebaseUser } = useAuthContext();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [dragHasInvalidFile, setDragHasInvalidFile] = useState(false);
  const [messageHistoryIndex, setMessageHistoryIndex] = useState(-1);
  const [currentMessage, setCurrentMessage] = useState(defaultValue);
  const { selectedAccount } = useContext(UserContext);

  // Helper to resize textarea based on content
  const resizeTextarea = useCallback(
    (textarea: HTMLTextAreaElement) => {
      textarea.style.height = 'auto';
      const lineHeight = 24;
      const maxHeight = lineHeight * (maxLines || (size === 'lg' ? 6 : 3));
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    },
    [size, maxLines],
  );

  // Sync currentMessage with defaultValue when it changes
  useEffect(() => {
    setCurrentMessage(defaultValue);
    if (textareaRef.current) {
      textareaRef.current.value = defaultValue;
      resizeTextarea(textareaRef.current);
    }
  }, [defaultValue, resizeTextarea]);

  // Create a compatible setState wrapper for child components
  const setPromptContextsCompat = useCallback(
    (value: React.SetStateAction<PromptAttachment[]>) => {
      if (typeof value === 'function') {
        setPromptContexts(value(promptContexts));
      } else {
        setPromptContexts(value);
      }
    },
    [setPromptContexts, promptContexts],
  );

  // Check if any files/figma exports are currently uploading or failed to upload
  const hasInvalidFiles = promptContexts.some(
    (ctx) =>
      (ctx.type === 'file' &&
        (ctx.uploadState?.isUploading ||
          ctx.uploadState?.error ||
          !ctx.uploadState?.uploadedUrl)) ||
      (ctx.type === 'figma' &&
        (ctx.uploadState?.isUploading ||
          ctx.uploadState?.error ||
          !ctx.screenshotUrl ||
          !ctx.structureUrl)),
  );

  const sendMessage = useCallback(
    (message: string, form?: HTMLFormElement) => {
      if (!message.trim() || disabled || hasInvalidFiles || isStreaming) return;

      // Transform PromptAttachment[] to AgentRunAttachment[] format
      // Strip frontend-only fields (localFile, uploadState) and ensure required fields are present
      const attachments: AgentRunAttachment[] = [];
      const contextTypeCounts = {
        campaign: 0,
        file: 0,
        element: 0,
        asset: 0,
        figma: 0,
      };

      promptContexts.forEach((ctx) => {
        switch (ctx.type) {
          case 'campaign': {
            // Campaign attachments are already in the correct format
            attachments.push({
              id: ctx.id,
              type: 'campaign',
              name: ctx.name,
              campaignId: ctx.campaignId,
              provider: ctx.provider,
            });
            contextTypeCounts.campaign++;
            break;
          }
          case 'file': {
            // Only include files that have finished uploading
            if (ctx.uploadState?.uploadedUrl && !ctx.uploadState?.isUploading) {
              const localFile = ctx.localFile;
              attachments.push({
                id: ctx.id,
                type: 'file',
                name: ctx.name || localFile?.name || 'Unknown file',
                url: ctx.uploadState.uploadedUrl,
                size: ctx.size || localFile?.size || 0,
                mimeType:
                  ctx.mimeType || localFile?.type || 'application/octet-stream',
              });
              contextTypeCounts.file++;
            }
            break;
          }
          case 'element': {
            // Element attachments are already in the correct format
            attachments.push({
              id: ctx.id,
              type: 'element',
              sectionId: ctx.sectionId,
              sectionType: ctx.sectionType,
              sectionIndex: ctx.sectionIndex,
              elementPath: ctx.elementPath,
              elementHTML: ctx.elementHTML,
              elementText: ctx.elementText,
              elementTag: ctx.elementTag,
              screenshot: ctx.screenshot,
              stepId: ctx.stepId,
              fieldName: ctx.fieldName,
            });
            contextTypeCounts.element++;
            break;
          }
          case 'asset': {
            // Asset attachments contain the nested asset object
            attachments.push({
              id: ctx.id,
              type: 'asset',
              asset: ctx.asset,
            });
            contextTypeCounts.asset++;
            break;
          }
          case 'figma': {
            // Only include figma exports that have finished uploading
            if (
              ctx.screenshotUrl &&
              ctx.structureUrl &&
              !ctx.uploadState?.isUploading
            ) {
              attachments.push({
                id: ctx.id,
                type: 'figma',
                name: ctx.name,
                screenshotUrl: ctx.screenshotUrl,
                structureUrl: ctx.structureUrl,
                width: ctx.width,
                height: ctx.height,
                layerCount: ctx.layerCount,
              });
              contextTypeCounts.figma++;
            }
            break;
          }
        }
      });

      // Track message submission
      track('chat_input_message_submitted', {
        message_length: message.trim().length,
        has_attachments: attachments.length > 0,
        total_attachments: attachments.length,
        campaign_count: contextTypeCounts.campaign,
        file_count: contextTypeCounts.file,
        element_count: contextTypeCounts.element,
        asset_count: contextTypeCounts.asset,
        figma_count: contextTypeCounts.figma,
        input_size: size,
        is_collapsed: collapsed,
      });

      onSendMessage?.(
        message,
        attachments.length > 0 ? attachments : undefined,
      );

      // Reset form and state
      form?.reset();
      setPromptContexts([]);
      setMessageHistoryIndex(-1);
      setCurrentMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    },
    [
      disabled,
      hasInvalidFiles,
      isStreaming,
      promptContexts,
      onSendMessage,
      setPromptContexts,
      track,
      size,
      collapsed,
    ],
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const message = formData.get('message') as string;
    sendMessage(message, e.currentTarget);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (disabled) return;

    const textarea = e.currentTarget;
    const { selectionStart, selectionEnd, value } = textarea;

    // Helper function to check if cursor is on first line
    const isOnFirstLine = () => {
      const textBeforeCursor = value.substring(0, selectionStart);
      return !textBeforeCursor.includes('\n');
    };

    // Helper function to check if cursor is on last line
    const isOnLastLine = () => {
      const textAfterCursor = value.substring(selectionEnd);
      return !textAfterCursor.includes('\n');
    };

    // Handle message history navigation
    if (
      e.key === 'ArrowUp' &&
      userMessageHistory.length > 0 &&
      isOnFirstLine()
    ) {
      e.preventDefault();
      const newIndex = Math.min(
        messageHistoryIndex + 1,
        userMessageHistory.length - 1,
      );
      if (newIndex !== messageHistoryIndex) {
        // Save current message if we're starting navigation
        if (messageHistoryIndex === -1) {
          setCurrentMessage(e.currentTarget.value);
          track('chat_input_history_navigation_started', {
            direction: 'up',
            total_history_items: userMessageHistory.length,
          });
        }
        setMessageHistoryIndex(newIndex);
        const historicalMessage = userMessageHistory[newIndex];
        e.currentTarget.value = historicalMessage;
        // Trigger resize
        handleInput(e as any);
        // Move cursor to end of text
        setTimeout(() => {
          e.currentTarget.setSelectionRange(
            historicalMessage.length,
            historicalMessage.length,
          );
        }, 0);
      }
      return;
    }

    if (e.key === 'ArrowDown' && isOnLastLine()) {
      e.preventDefault();
      if (messageHistoryIndex > 0) {
        const newIndex = messageHistoryIndex - 1;
        setMessageHistoryIndex(newIndex);
        const historicalMessage = userMessageHistory[newIndex];
        e.currentTarget.value = historicalMessage;
        // Trigger resize
        handleInput(e as any);
        // Move cursor to end of text
        setTimeout(() => {
          e.currentTarget.setSelectionRange(
            historicalMessage.length,
            historicalMessage.length,
          );
        }, 0);
      } else if (messageHistoryIndex === 0) {
        // Return to current message
        setMessageHistoryIndex(-1);
        e.currentTarget.value = currentMessage;
        // Trigger resize
        handleInput(e as any);
        // Move cursor to end of text
        setTimeout(() => {
          e.currentTarget.setSelectionRange(
            currentMessage.length,
            currentMessage.length,
          );
        }, 0);
      }
      return;
    }

    // Reset history navigation when user types
    if (
      messageHistoryIndex !== -1 &&
      e.key !== 'ArrowUp' &&
      e.key !== 'ArrowDown' &&
      e.key !== 'Shift' &&
      e.key !== 'Control' &&
      e.key !== 'Alt' &&
      e.key !== 'Meta'
    ) {
      setMessageHistoryIndex(-1);
      setCurrentMessage('');
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      const form = e.currentTarget.form;
      if (form) {
        const formData = new FormData(form);
        const message = formData.get('message') as string;
        sendMessage(message, form);
      }
    }
  };

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    resizeTextarea(e.currentTarget);
  };

  /**
   * Intercept paste events to detect SuperFunnel Figma exports.
   * The Figma plugin writes a custom HTML element to the clipboard containing
   * the design JSON as a base64-encoded data attribute. Normal paste destinations
   * only see the text/plain "Paste this in the SuperFunnel agent chat" message.
   *
   * Each selected Figma element becomes a separate `figma` attachment chip.
   */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Try custom MIME type first (new approach)
      const customData = e.clipboardData.getData(
        'application/superfunnel+json',
      );

      // Fallback: check text/html for legacy clipboard format
      const html = e.clipboardData.getData('text/html');
      const htmlMatch = html
        ? html.match(/data-version="1"\s+data-payload="([^"]+)"/)
        : null;

      if (!customData && !htmlMatch) return; // Not a Figma export — let normal paste proceed

      e.preventDefault();

      try {
        let parsed: any;
        if (customData) {
          // Custom MIME type — JSON string directly
          parsed = JSON.parse(customData);
        } else {
          // Legacy HTML approach — decode base64
          const encoded = htmlMatch![1];
          const json = decodeURIComponent(escape(atob(encoded)));
          parsed = JSON.parse(json);
        }

        // Support both array (multi-element) and single object (legacy)
        const payloads: any[] = Array.isArray(parsed) ? parsed : [parsed];

        for (const payload of payloads) {
          if (!payload._app || payload._app !== 'superfunnel-agent') continue;

          const contextId = `figma-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

          // Create figma attachment immediately with upload state
          const attachment: PromptAttachment = {
            id: contextId,
            type: 'figma' as const,
            name: payload.meta?.name || 'Figma Export',
            width: payload.meta?.width || 0,
            height: payload.meta?.height || 0,
            layerCount: payload.meta?.layerCount || 0,
            screenshotDataUri: payload.image,
            uploadState: {
              isUploading: true,
              progress: 0,
            },
          };

          addPromptContext(attachment);

          // Upload screenshot + structure JSON in background
          uploadFigmaExport(payload, contextId).catch((error) => {
            console.error('Failed to upload Figma export:', error);
          });
        }

        track('chat_input_figma_pasted', {
          element_count: payloads.length,
          total_layers: payloads.reduce(
            (sum: number, p: any) => sum + (p.meta?.layerCount || 0),
            0,
          ),
        });
      } catch (err) {
        console.error('Failed to parse Figma export from clipboard:', err);
      }
    },
    [addPromptContext, track],
  );

  /**
   * Upload a Figma export's screenshot and structure JSON to Firebase Storage.
   * Updates the prompt context with URLs when done.
   */
  const uploadFigmaExport = useCallback(
    async (payload: any, contextId: string) => {
      if (!selectedAccount) throw new Error('No account selected');
      if (!firebaseUser) throw new Error('No user logged in');

      const userId = firebaseUser.uid;
      const storagePath = `accounts/${selectedAccount.id}/users/${userId}/agentFiles/${threadId}`;

      try {
        // 1. Upload screenshot (data URI → blob)
        const screenshotDataUri = payload.image as string;
        const screenshotBase64 = screenshotDataUri.split(',')[1] || '';
        const screenshotBytes = Uint8Array.from(atob(screenshotBase64), (c) =>
          c.charCodeAt(0),
        );
        const screenshotBlob = new Blob([screenshotBytes], {
          type: 'image/jpeg',
        });
        const screenshotFile = new File(
          [screenshotBlob],
          `${contextId}-screenshot.jpg`,
          { type: 'image/jpeg' },
        );

        const screenshotRef = ref(
          agentFilesStorage,
          `${storagePath}/${Date.now()}-${contextId}-screenshot.jpg`,
        );
        const screenshotTask = uploadBytesResumable(
          screenshotRef,
          screenshotFile,
        );

        // Track screenshot upload progress (0-50%)
        screenshotTask.on('state_changed', (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 50;
          updatePromptContext(contextId, {
            uploadState: { isUploading: true, progress },
          } as any);
        });
        await screenshotTask;
        const screenshotUrl = await getDownloadURL(screenshotTask.snapshot.ref);

        // 2. Upload structure JSON (strip the image data URI to save space)
        const structurePayload = { ...payload };
        delete structurePayload.image; // Don't duplicate the screenshot in the JSON
        const structureJson = JSON.stringify(structurePayload, null, 2);
        const structureFile = new File(
          [structureJson],
          `${contextId}-structure.json`,
          { type: 'application/json' },
        );

        const structureRef = ref(
          agentFilesStorage,
          `${storagePath}/${Date.now()}-${contextId}-structure.json`,
        );
        const structureTask = uploadBytesResumable(structureRef, structureFile);

        // Track structure upload progress (50-100%)
        structureTask.on('state_changed', (snapshot) => {
          const progress =
            50 + (snapshot.bytesTransferred / snapshot.totalBytes) * 50;
          updatePromptContext(contextId, {
            uploadState: { isUploading: true, progress },
          } as any);
        });
        await structureTask;
        const structureUrl = await getDownloadURL(structureTask.snapshot.ref);

        // 3. Update attachment with uploaded URLs
        updatePromptContext(contextId, {
          screenshotUrl,
          structureUrl,
          uploadState: {
            isUploading: false,
            progress: 100,
            uploadedUrl: structureUrl,
          },
        } as any);
      } catch (error: any) {
        updatePromptContext(contextId, {
          uploadState: {
            isUploading: false,
            progress: 0,
            error: error.message || 'Upload failed',
          },
        } as any);
        throw error;
      }
    },
    [selectedAccount, firebaseUser, threadId, updatePromptContext],
  );

  const uploadAgentFile = useCallback(
    async (file: File, contextId: string): Promise<string> => {
      if (!selectedAccount) throw new Error('No account selected');
      if (!firebaseUser) throw new Error('No user logged in');

      const userId = firebaseUser.uid;

      // Compress large images client-side before uploading (Anthropic 5 MB limit)
      const fileToUpload = await compressImageForUpload(file);

      // If the file was compressed, update the attachment with the new mime type
      if (fileToUpload !== file) {
        updatePromptContext(contextId, {
          localFile: fileToUpload,
          mimeType: fileToUpload.type,
        } as any);
      }

      track('chat_input_file_upload_started', {
        file_name: fileToUpload.name,
        file_type: fileToUpload.type,
        file_size: fileToUpload.size,
        context_id: contextId,
        was_compressed: fileToUpload !== file,
      });

      return new Promise((resolve, reject) => {
        const fileExt = fileToUpload.name.split('.').pop();
        const fileName = `${Date.now()}-${contextId}${fileExt ? `.${fileExt}` : ''}`;
        const storagePath = `accounts/${selectedAccount.id}/users/${userId}/agentFiles/${threadId}/${fileName}`;

        const storageRef = ref(agentFilesStorage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            updatePromptContext(contextId, {
              uploadState: {
                isUploading: true,
                progress,
              },
            } as any);
          },
          (error) => {
            track('chat_input_file_upload_failed', {
              file_name: fileToUpload.name,
              file_type: fileToUpload.type,
              file_size: fileToUpload.size,
              context_id: contextId,
              error_message: error.message,
            });
            updatePromptContext(contextId, {
              uploadState: {
                isUploading: false,
                progress: 0,
                error: error.message,
              },
            } as any);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              track('chat_input_file_upload_completed', {
                file_name: fileToUpload.name,
                file_type: fileToUpload.type,
                file_size: fileToUpload.size,
                context_id: contextId,
              });
              updatePromptContext(contextId, {
                uploadState: {
                  isUploading: false,
                  progress: 100,
                  uploadedUrl: downloadURL,
                },
              } as any);
              resolve(downloadURL);
            } catch (error: any) {
              track('chat_input_file_upload_failed', {
                file_name: fileToUpload.name,
                file_type: fileToUpload.type,
                file_size: fileToUpload.size,
                context_id: contextId,
                error_message: error.message,
              });
              updatePromptContext(contextId, {
                uploadState: {
                  isUploading: false,
                  progress: 0,
                  error: error.message,
                },
              } as any);
              reject(error);
            }
          },
        );
      });
    },
    [selectedAccount, firebaseUser, threadId, updatePromptContext, track],
  );

  const handleFileUpload = useCallback(
    (f: File[]) => {
      if (!selectedAccount) return;
      if (f.length) {
        track('chat_input_files_added', {
          file_count: f.length,
          file_types: f.map((file) => file.type),
          total_size: f.reduce((sum, file) => sum + file.size, 0),
        });

        f.forEach((file) => {
          const contextId = `file-${Date.now()}-${Math.random()}`;
          const attachment: PromptAttachment = {
            id: contextId,
            type: 'file' as const,
            localFile: file,
            uploadState: {
              isUploading: true,
              progress: 0,
            },
          };

          // Add attachment first
          addPromptContext(attachment);

          // Start upload immediately
          uploadAgentFile(file, contextId).catch((error) => {
            console.error('Failed to upload file:', error);
          });
        });
      }
    },
    [selectedAccount, uploadAgentFile, addPromptContext, track],
  );

  const handleDrag = function (e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      if (!dragActive) {
        track('chat_input_drag_entered', {
          input_size: size,
        });
      }
      setDragActive(true);

      // Check if any dragged item is invalid
      // During dragover, webkitGetAsEntry isn't reliable, so we use heuristics:
      // - Folders have kind='file' but empty type
      // - Videos, audio, and archives are blocked
      const items = e.dataTransfer.items;
      if (items) {
        let hasInvalidFile = false;
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file') {
            const type = item.type;
            // Folders have empty type, or type is blocked
            if (type === '' || isBlockedMimeType(type)) {
              hasInvalidFile = true;
              break;
            }
          }
        }
        setDragHasInvalidFile(hasInvalidFile);
      }
    } else if (e.type === 'dragleave') {
      setDragActive(false);
      setDragHasInvalidFile(false);
    }
  };

  const handleFileDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setDragActive(false);
      setDragHasInvalidFile(false);

      // Filter out invalid files (folders, videos, audio, archives, oversized)
      const files: File[] = [];
      const items = e.dataTransfer.items;

      const isValidFile = (file: File): boolean => {
        // Check file type
        if (isBlockedMimeType(file.type)) {
          return false;
        }
        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          return false;
        }
        return true;
      };

      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          // webkitGetAsEntry returns null for non-file items, and entry.isFile for files
          const entry = item.webkitGetAsEntry?.();
          if (entry && entry.isFile) {
            const file = item.getAsFile();
            if (file && isValidFile(file)) {
              files.push(file);
            }
          }
        }
      } else if (e.dataTransfer.files?.length) {
        // Fallback for browsers that don't support items
        // Filter by checking if file has a type or size > 0 (folders typically have neither)
        Array.from(e.dataTransfer.files).forEach((file) => {
          if ((file.type || file.size > 0) && isValidFile(file)) {
            files.push(file);
          }
        });
      }

      if (files.length > 0) {
        track('chat_input_files_dropped', {
          file_count: files.length,
          input_size: size,
        });
        handleFileUpload(files);
      }

      e.dataTransfer.clearData();
    },
    [handleFileUpload, track, size],
  );

  return (
    <div className={className}>
      <form
        onSubmit={handleSubmit}
        className={classNames(
          'cursor-text bg-white',
          collapsed ? 'rounded-2xl' : 'rounded-[1.25rem]',
          variant === 'outline' && 'border-base-200 border',
        )}
        onClick={() => {
          // Focus the textarea when clicking anywhere in the form
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        }}
      >
        {/* Content + Drag and Drop Area */}
        <div
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleFileDrop}
          className={classNames(
            'w-full overflow-hidden',
            size === 'sm' ? 'p-2' : 'p-4',
            'relative rounded-[1.25rem] border border-dashed',
            dragActive && dragHasInvalidFile
              ? 'border-red-400 bg-red-50'
              : dragActive
                ? 'bg-brand-50 border-brand-500'
                : 'border-transparent',
          )}
        >
          {/* Invalid file type error message */}
          {dragActive && dragHasInvalidFile && (
            <div className="mb-2 text-center text-sm text-red-600">
              Only images, PDFs, and documents are supported.
            </div>
          )}
          {promptContexts.length > 0 && (
            <ChatInputContextsList
              size={size}
              promptContexts={promptContexts}
              setPromptContexts={setPromptContextsCompat}
            />
          )}

          <div
            className={classNames(
              'grid transition-all duration-300 ease-in-out',
              collapsed
                ? 'grid-cols-[auto_1fr_auto] grid-rows-1 items-center gap-2'
                : 'grid-cols-[auto_1fr] grid-rows-[auto_auto] gap-x-2',
            )}
          >
            {/* Textarea - spans both columns when expanded, center column when collapsed */}
            <textarea
              ref={textareaRef}
              name="message"
              placeholder={placeholder}
              rows={collapsed ? 1 : size === 'sm' ? 1 : 2}
              onKeyDown={handleKeyDown}
              onInput={handleInput}
              onPaste={handlePaste}
              className={classNames(
                'font-inter placeholder:text-base-400 resize-none border-none bg-transparent py-2 -tracking-[0.01em] transition-all duration-300 ease-in-out outline-none focus:ring-0 focus:outline-none',
                collapsed
                  ? 'col-start-2 row-start-1 flex-1 p-0 text-sm'
                  : size === 'sm'
                    ? 'col-span-2 row-start-1 w-full p-0 ps-2 text-sm'
                    : 'col-span-2 row-start-1 w-full px-2 py-1 text-base',
              )}
            />

            {/* Context buttons - left column second row when expanded, first column when collapsed */}
            <div
              className={classNames(
                'flex items-end transition-all duration-300 ease-in-out',
                collapsed
                  ? 'col-start-1 row-start-1 gap-1'
                  : 'col-start-1 row-start-2 gap-1',
                !collapsed && (size === 'sm' ? 'mt-2' : 'mt-4.5'),
              )}
            >
              <ChatInputContextButtonsList
                contextTypes={contextTypes}
                size={size}
                promptContexts={promptContexts}
                setPromptContexts={setPromptContextsCompat}
                handleFileUpload={handleFileUpload}
                onToggleVisualEdit={onToggleVisualEdit}
                isVisualEditMode={isVisualEditMode}
                track={track}
              />
            </div>

            {/* Action button - right column second row when expanded, third column when collapsed */}
            <div
              className={classNames(
                'flex items-center transition-all duration-300 ease-in-out',
                collapsed
                  ? 'col-start-3 row-start-1'
                  : 'col-start-2 row-start-2 justify-end gap-5',
                !collapsed && (size === 'sm' ? 'mt-2' : 'mt-4.5'),
              )}
            >
              {isStreaming ? (
                <Button
                  type="button"
                  size="icon"
                  color="default"
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    track('chat_input_stop_clicked', {
                      input_size: size,
                      is_collapsed: collapsed,
                    });
                    onStopMessage?.();
                  }}
                  className={classNames(
                    'bg-base-100 rounded-2lg hover:bg-base-200 size-10',
                    !collapsed && size === 'sm' ? 'ml-2' : '',
                  )}
                >
                  <div
                    className={classNames(
                      'bg-base-500 rounded',
                      size === 'sm' ? 'size-4' : 'size-5',
                    )}
                  />
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="icon"
                  disabled={disabled || hasInvalidFiles}
                  className={classNames(
                    'rounded-2lg size-10',
                    !collapsed && size === 'sm' ? 'ml-2' : '',
                  )}
                  title={
                    hasInvalidFiles
                      ? promptContexts.some(
                          (ctx) =>
                            ctx.type === 'file' && ctx.uploadState?.isUploading,
                        )
                        ? 'Please wait for files to finish uploading'
                        : 'Some files failed to upload. Please remove them or try again'
                      : undefined
                  }
                >
                  <img src={arrowUp} className="size-6" alt="Arrow Up" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

function ChatInputContextButtonsList({
  contextTypes,
  size,
  promptContexts,
  setPromptContexts,
  handleFileUpload,
  onToggleVisualEdit,
  isVisualEditMode,
  track,
}: {
  contextTypes: ContextInput[];
  size: 'sm' | 'lg';
  promptContexts: PromptAttachment[];
  setPromptContexts: (value: React.SetStateAction<PromptAttachment[]>) => void;
  handleFileUpload: (files: File[]) => void;
  onToggleVisualEdit?: () => void;
  isVisualEditMode?: boolean;
  track: (event: string, properties?: Record<string, any>) => void;
}) {
  // Filter context types by location
  // 'dropdown' = inside the context selector/picker
  // 'inline' = standalone buttons in the toolbar
  const dropdownContextTypes = contextTypes
    .filter((ctx) => ctx.location === 'dropdown')
    .filter(
      (ctx) =>
        ctx.type === 'file' || ctx.type === 'campaign' || ctx.type === 'asset',
    )
    .map((ctx) => ctx.type as 'file' | 'campaign' | 'asset');

  const inlineContextTypes = contextTypes
    .filter((ctx) => ctx.location === 'inline')
    .map((ctx) => ctx.type);

  return (
    <>
      {/* Show context selector button if any contexts should be in dropdown */}
      {!!dropdownContextTypes.length && (
        <ChatInputContextSelectButton
          size={size}
          contextTypes={dropdownContextTypes}
          promptContexts={promptContexts}
          setPromptContexts={setPromptContexts}
          onFileUpload={handleFileUpload}
        />
      )}

      {/* Inline buttons - displayed as separate buttons in toolbar */}
      {inlineContextTypes.includes('element') && onToggleVisualEdit && (
        <Button
          type="button"
          variant="ghost"
          size={'sm'}
          className={classNames(
            isVisualEditMode
              ? 'bg-brand-50 text-brand-700 hover:bg-brand-100'
              : '',
          )}
          onClick={() => {
            track('chat_input_visual_edit_toggled', {
              enabled: !isVisualEditMode,
              size,
            });
            onToggleVisualEdit();
          }}
          title={
            isVisualEditMode ? 'Exit selection mode' : 'Select element to edit'
          }
        >
          <SquareCursor className="size-5" />
          <span className="text-sm tracking-tight">Visual edit</span>
        </Button>
      )}
      {inlineContextTypes.includes('campaign') && (
        <ChatInputSelectCampaign
          size={size}
          promptContexts={promptContexts}
          setPromptContexts={setPromptContexts}
        />
      )}
    </>
  );
}
